import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { WebsocketService } from '../websocket.service';
import { Loggable } from 'app/utils/logging/loggable';
import {
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
  SharingStartedMessage,
  SharingEndedMessage,
  SharingAnswerMessage,
  SharingOfferMessage,
  SharingPublishServerCandidateMessage,
  SharingSubscriptionCandidateMessage,
  PeerCandidate,
  PeerJsep,
} from 'messages/websocket.messages';

/**
 * Service that centralizes all WebSocket messaging for group screen sharing.
 * This avoids duplicate message handling across multiple services.
 */
@Injectable({ providedIn: 'root' })
export class GroupShareScreenMessageService extends Loggable {
  private websocketService = inject(WebsocketService);

  // Track active subscriptions to prevent duplicate messages
  private activeSubscriptions = new Set<string>();
  private subscriptionTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  // Message subjects
  private readonly shareStartedSubject = new Subject<SharingStartedMessage>();
  private readonly shareEndedSubject = new Subject<SharingEndedMessage>();
  private readonly shareAnswerSubject = new Subject<SharingAnswerMessage>();
  private readonly shareOfferSubject = new Subject<SharingOfferMessage>();
  private readonly publisherCandidateSubject = new Subject<SharingPublishServerCandidateMessage>();
  private readonly subscriberCandidateSubject = new Subject<SharingSubscriptionCandidateMessage>();

  // Exposed as observables
  public readonly shareStarted$ = this.shareStartedSubject.asObservable();
  public readonly shareEnded$ = this.shareEndedSubject.asObservable();
  public readonly shareAnswer$ = this.shareAnswerSubject.asObservable();
  public readonly shareOffer$ = this.shareOfferSubject.asObservable();
  public readonly publisherCandidate$ = this.publisherCandidateSubject.asObservable();
  public readonly subscriberCandidate$ = this.subscriberCandidateSubject.asObservable();

  public constructor() {
    super();
    this.setDisplayName('GroupShareScreenMessageService');

    this.websocketService.message$
      .pipe(filter((msg) => this.isGroupShareMessage(msg)))
      .subscribe((msg) => this.routeMessage(msg));
  }

  private readonly groupShareMessageTypes: ReadonlySet<IncomingMessageType> = new Set([
    IncomingMessageType.SHR_STARTED,
    IncomingMessageType.SHR_ENDED,
    IncomingMessageType.SHR_PUBANS,
    IncomingMessageType.SHR_SUBOFF,
    IncomingMessageType.SHR_PUBCAN,
    IncomingMessageType.SHR_SUBCAN,
  ]);

  /**
   * Determines if a message is related to group screen sharing
   */
  private isGroupShareMessage(msg: IncomingMessage): boolean {
    if (!this.groupShareMessageTypes.has(msg.t)) {
      return false;
    }

    // Special case for SHR_STARTED which needs groupShare flag check
    if (msg.t === IncomingMessageType.SHR_STARTED) {
      return 'groupShare' in msg && !!msg.groupShare;
    }

    return true;
  }

  private getLogMessage(messageType: IncomingMessageType): string {
    switch (messageType) {
      case IncomingMessageType.SHR_STARTED:
        return 'Group share started message received';
      case IncomingMessageType.SHR_ENDED:
        return 'Group share ended message received';
      case IncomingMessageType.SHR_PUBANS:
        return 'Group share publish answer received';
      case IncomingMessageType.SHR_SUBOFF:
        return 'Group share subscribe offer received';
      case IncomingMessageType.SHR_PUBCAN:
        return 'Group share publisher candidate received';
      case IncomingMessageType.SHR_SUBCAN:
        return 'Group share subscriber candidate received';
      default:
        return 'Group share message received';
    }
  }

  /**
   * Routes incoming WebSocket messages to the appropriate subject
   */
  private routeMessage(msg: IncomingMessage): void {
    this.info(this.getLogMessage(msg.t), msg);

    switch (msg.t) {
      case IncomingMessageType.SHR_STARTED:
        this.shareStartedSubject.next(msg as SharingStartedMessage);
        break;
      case IncomingMessageType.SHR_ENDED:
        this.shareEndedSubject.next(msg as SharingEndedMessage);
        break;
      case IncomingMessageType.SHR_PUBANS:
        this.shareAnswerSubject.next(msg as SharingAnswerMessage);
        break;
      case IncomingMessageType.SHR_SUBOFF:
        this.shareOfferSubject.next(msg as SharingOfferMessage);
        break;
      case IncomingMessageType.SHR_PUBCAN:
        this.publisherCandidateSubject.next(msg as SharingPublishServerCandidateMessage);
        break;
      case IncomingMessageType.SHR_SUBCAN:
        this.subscriberCandidateSubject.next(msg as SharingSubscriptionCandidateMessage);
        break;
    }
  }

  /**
   * Helper method to send WebSocket messages with groupShare flag
   */
  private sendGroupShareMessage<T extends object>(messageType: OutgoingMessageType, payload: T): void {
    this.websocketService.sendMessage({
      t: messageType,
      ...payload,
      groupShare: true,
    });
  }

  /**
   * Sends a message to start screen sharing
   */
  public sendStartSharingMessage(offer: PeerJsep, bitrate: number): void {
    this.sendGroupShareMessage(OutgoingMessageType.SHR, { offer, bitrate });
  }

  /**
   * Sends a message to end screen sharing
   */
  public sendEndSharingMessage(socialId: string): void {
    this.sendGroupShareMessage(OutgoingMessageType.SHREND, { socialId });
  }

  /**
   * Sends a candidate for the publisher
   */
  public sendPublisherCandidate(userId: string, candidate: PeerCandidate): void {
    this.sendGroupShareMessage(OutgoingMessageType.SHRCAN, { userId, d: candidate });
  }

  /**
   * Sends a subscribe message to view someone else's screen share
   * Includes debounce to prevent duplicate messages
   */
  public sendSubscribeMessage(sharingUserId: string): void {
    // If we already have an active subscription for this user, don't send another message
    if (this.activeSubscriptions.has(sharingUserId)) {
      this.info('Skipping duplicate subscription message for user', { sharingUserId });
      return;
    }

    // Clear any existing timeout for this user
    if (this.subscriptionTimeouts.has(sharingUserId)) {
      clearTimeout(this.subscriptionTimeouts.get(sharingUserId));
      this.subscriptionTimeouts.delete(sharingUserId);
    }

    // Add to active subscriptions
    this.activeSubscriptions.add(sharingUserId);

    // Send the message
    this.info('Sending subscription message for user', { sharingUserId });
    this.sendGroupShareMessage(OutgoingMessageType.SHRSUB, { sharingUserId });

    // Set a timeout to remove from active subscriptions after 5 seconds
    // This allows future subscription attempts if needed
    const timeout = setTimeout(() => {
      this.activeSubscriptions.delete(sharingUserId);
      this.subscriptionTimeouts.delete(sharingUserId);
    }, 5000);

    this.subscriptionTimeouts.set(sharingUserId, timeout);
  }

  /**
   * Sends a subscribe answer message
   */
  public sendSubscribeAnswerMessage(sharingUserId: string, answer: PeerJsep): void {
    this.sendGroupShareMessage(OutgoingMessageType.SHRSUBANS, { sharingUserId, answer });
  }

  /**
   * Sends a candidate for the subscriber
   */
  public sendSubscriberCandidate(sharingUserId: string, d: PeerCandidate): void {
    this.sendGroupShareMessage(OutgoingMessageType.SHRSUBCAN, { d, sharingUserId });
  }

  /**
   * Sends a message to unsubscribe from a screen share
   */
  public sendUnsubscribeMessage(sharingUserId: string): void {
    // Clean up subscription tracking
    this.activeSubscriptions.delete(sharingUserId);

    // Clear any existing timeout
    if (this.subscriptionTimeouts.has(sharingUserId)) {
      clearTimeout(this.subscriptionTimeouts.get(sharingUserId));
      this.subscriptionTimeouts.delete(sharingUserId);
    }

    this.info('Sending unsubscribe message for user', { sharingUserId });
    this.sendGroupShareMessage(OutgoingMessageType.SHRUNSUB, { sharingUserId });
  }
}
